import { useState } from "react";
import useDeadlineAlerts from "../hooks/useDeadlineAlerts.js";
import useNegotiationAlerts from "../hooks/useNegotiationAlerts.js";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Container,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  AccountCircleOutlined,
  AlarmOutlined,
  CalendarMonthOutlined,
  CompareArrows,
  FolderOpenOutlined,
  Gavel,
  Groups3Outlined,
  HomeOutlined,
  KeyboardArrowDown,
  Logout,
  MenuOpen,
  Menu as MenuIcon,
  PeopleOutline,
  WorkOutline,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../actions/auth.js";

// Tailwind class form of the accent gradient (linear-gradient(120deg, #2f6fd6 0%, #1fb5c9 100%))
const ACCENT_CLASS = "bg-[linear-gradient(120deg,#2f6fd6_0%,#1fb5c9_100%)]";

// Role rules are unchanged from the previous sidebar.
const NAV_SECTIONS = [
  {
    title: "Overview",
    items: [
      { text: "Dashboard", to: "/dashboard", Icon: HomeOutlined, roles: /^(admin|registrar|trial_user)$/i },
      { text: "Profile", to: "/profile", Icon: AccountCircleOutlined },
    ],
  },
  {
    title: "Cases",
    items: [
      { text: "Cases", to: "/cases", Icon: WorkOutline },
      { text: "Timeline", to: "/timeline", Icon: TimelineIcon },
      { text: "Deadlines", to: "/deadlines", Icon: AlarmOutlined },
      { text: "Documents", to: "/documents", Icon: FolderOpenOutlined },
      { text: "Negotiations", to: "/negotiation", Icon: CompareArrows },
      { text: "Calendar", to: "/calendar", Icon: CalendarMonthOutlined },
    ],
  },
  {
    title: "Administration",
    items: [
      { text: "Users", to: "/users", Icon: PeopleOutline, roles: /^(admin|registrar)$/i },
      // Questionnaire gate removed from New Case; routes and data are kept, so these can be switched back on.
      // { text: "Survey Questions", to: "/questions", Icon: QuizOutlined, roles: /^(admin|registrar|trial_user)$/i },
      // { text: "Submitted Q/A", to: "/submittedques", Icon: FactCheckOutlined, roles: /^(admin|registrar|trial_user)$/i },
      { text: "Customers", to: "/customer", Icon: Groups3Outlined, roles: /^admin$/i },
    ],
  },
];

const PAGE_TITLES = {
  dashboard: "Dashboard",
  profile: "Profile",
  users: "Users",
  meetings: "Meetings",
  cases: "Cases",
  documents: "Documents",
  questions: "Survey Questions",
  submittedques: "Submitted Q/A",
  customer: "Customers",
  calendar: "Calendar",
  timeline: "Case Timeline",
  deadlines: "Deadlines",
  negotiation: "Negotiations",
  "dispute-items": "Dispute Items",
  payment: "Payment",
};

function isActive(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function initials(user) {
  const first = user?.firstname?.[0] || user?.email?.[0] || "";
  const last = user?.lastname?.[0] || "";
  return `${first}${last}`.toUpperCase() || "?";
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function NavItem({ item, active, collapsed, alerts, negAlerts }) {
  const { Icon, text, to } = item;
  const badge = item.to === "/deadlines" ? alerts : null;
  const isNeg = item.to === "/negotiation";
  const badgeCount = isNeg ? negAlerts.count : badge ? badge.overdue + badge.dueSoon : 0;
  const badgeColor = isNeg ? "warning" : badge?.overdue ? "error" : "warning";
  const tip = isNeg
    ? `${text} · ${badgeCount} awaiting your response`
    : `${text} · ${badge?.overdue} overdue, ${badge?.dueSoon} due soon`;
  const link = (
    <Box
      component={Link}
      to={to}
      // styles.css forces `a { color: inherit !important }`, so colour the children instead
      className={[
        "flex items-center gap-3 mx-3 mb-1 py-[8.8px] rounded-[10px] no-underline [transition:background-color_0.2s_ease] [&>*]:[transition:color_0.2s_ease] hover:[&>*]:text-white",
        collapsed ? "px-0 justify-center" : "px-3 justify-start",
        active
          ? "bg-[linear-gradient(120deg,#2f6fd6_0%,#1fb5c9_100%)] shadow-[0_8px_20px_-8px_rgba(31,181,201,0.7)] [&>*]:text-white"
          : "bg-transparent shadow-none [&>*]:text-[rgba(255,255,255,0.72)] hover:bg-[rgba(255,255,255,0.08)]",
      ].join(" ")}
    >
      <Badge
        badgeContent={badgeCount}
        color={badgeColor}
        max={99}
        invisible={!badgeCount}
        overlap="circular"
        className="[&_.MuiBadge-badge]:!text-white [&_.MuiBadge-badge]:!text-[10px] [&_.MuiBadge-badge]:!h-4 [&_.MuiBadge-badge]:!min-w-4"
      >
        <Icon className="!text-[21px]" />
      </Badge>
      {!collapsed ? (
        <Typography fontSize={14} fontWeight={active ? 600 : 500} noWrap>
          {text}
        </Typography>
      ) : null}
    </Box>
  );

  return collapsed ? (
    <Tooltip title={badgeCount ? tip : text} placement="right" arrow>
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function Sidebar({ collapsed, user, pathname, alerts, negAlerts }) {
  const role = user?.roles || "";
  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.roles || item.roles.test(role)),
  })).filter((section) => section.items.length);

  return (
    <Box
      component="nav"
      className={`${
        collapsed ? "w-[76px]" : "w-[248px]"
      } shrink-0 h-screen sticky top-0 flex flex-col text-white overflow-x-hidden bg-[linear-gradient(180deg,#061a3a_0%,#0a2a5e_60%,#0b3474_100%)] border-r border-solid border-r-[rgba(255,255,255,0.06)] [transition:width_0.25s_ease] z-[2]`}
    >
      <Box
        className={`h-[64px] flex items-center gap-3 shrink-0 ${collapsed ? "px-0 justify-center" : "px-5 justify-start"}`}
      >
        <Box
          className={`w-[38px] h-[38px] rounded-[10px] grid place-items-center ${ACCENT_CLASS} shadow-[0_8px_18px_-6px_rgba(31,181,201,0.8)] shrink-0`}
        >
          <Gavel className="!text-[20px]" />
        </Box>
        {!collapsed ? (
          <Box>
            <Typography fontWeight={700} fontSize={18} lineHeight={1.1}>
              ODR
            </Typography>
            <Typography fontSize={11} className="opacity-60" noWrap>
              Online Dispute Resolution
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box
        className="flex-1 overflow-y-auto overflow-x-hidden py-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-[rgba(255,255,255,0.15)] [&::-webkit-scrollbar-thumb]:rounded-lg"
      >
        {sections.map((section) => (
          <Box key={section.title} mb={1.5}>
            {collapsed ? (
              <Divider className="!border-[rgba(255,255,255,0.08)] mx-4 mb-2" />
            ) : (
              <Typography
                className="px-6 py-2 text-[11px] font-semibold tracking-[0.08em] uppercase text-[rgba(255,255,255,0.4)]"
              >
                {section.title}
              </Typography>
            )}
            {section.items.map((item) => (
              <NavItem key={item.to} item={item} collapsed={collapsed} alerts={alerts} negAlerts={negAlerts} active={isActive(pathname, item.to)} />
            ))}
          </Box>
        ))}
      </Box>

      <Box
        className={`m-3 rounded-xl flex items-center gap-[9.6px] bg-[rgba(255,255,255,0.06)] border border-solid border-[rgba(255,255,255,0.08)] shrink-0 ${
          collapsed ? "p-[6px] justify-center" : "p-3 justify-start"
        }`}
      >
        <Avatar className={`w-[34px] h-[34px] text-[14px] font-semibold ${ACCENT_CLASS}`}>
          {initials(user)}
        </Avatar>
        {!collapsed ? (
          <Box minWidth={0}>
            <Typography fontSize={13} fontWeight={600} noWrap>
              {[user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email}
            </Typography>
            <Typography fontSize={11} className="opacity-60 capitalize" noWrap>
              {String(role).replace(/_/g, " ").toLowerCase()}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default function Layout({ children }) {
  const { data: user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSmall = useMediaQuery("(max-width: 900px)", { noSsr: true });

  const [collapsed, setCollapsed] = useState(isSmall);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const alerts = useDeadlineAlerts(user?.token, pathname);
  const negAlerts = useNegotiationAlerts(user?.token, pathname);
  const pageTitle = PAGE_TITLES[pathname.split("/")[1]] || "ODR";
  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ");

  return (
    <Box className="flex h-screen overflow-hidden bg-[#f4f6fb]">
      <CssBaseline />
      <Sidebar collapsed={collapsed} user={user} pathname={pathname} alerts={alerts} negAlerts={negAlerts} />

      <Box component="main" className="flex-1 min-w-0 flex flex-col h-screen overflow-auto">
        <Box
          component="header"
          className="sticky top-0 z-[1100] h-[64px] shrink-0 flex items-center gap-4 px-3 min-[600px]:px-6 bg-[rgba(255,255,255,0.8)] backdrop-blur-[14px] border-b border-solid border-b-[#e6eaf2]"
        >
          <IconButton
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="text-[#0b3474] bg-[#eef3fb] rounded-[10px] hover:bg-[#e1eaf8]"
          >
            {collapsed ? <MenuIcon /> : <MenuOpen />}
          </IconButton>

          <Box minWidth={0}>
            <Typography fontWeight={700} fontSize={18} color="#0d1b36" lineHeight={1.2} noWrap>
              {pageTitle}
            </Typography>
            <Typography fontSize={12} color="text.secondary" noWrap className="hidden min-[600px]:block">
              {greeting()}
              {user?.firstname ? `, ${user.firstname}` : ""}
            </Typography>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-controls={menuOpen ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            className="ml-auto flex items-center gap-[9.6px] pl-[4.8px] pr-[9.6px] py-[4.8px] border border-solid border-[#e6eaf2] rounded-[40px] bg-white cursor-pointer [font:inherit] [transition:box-shadow_0.2s_ease] hover:shadow-[0_4px_14px_-6px_rgba(11,52,116,0.35)]"
          >
            <Avatar className={`w-[34px] h-[34px] text-[14px] font-semibold ${ACCENT_CLASS}`}>
              {initials(user)}
            </Avatar>
            <Box textAlign="left" className="hidden min-[600px]:block">
              <Typography fontSize={13} fontWeight={600} color="#0d1b36" lineHeight={1.2} noWrap maxWidth={160}>
                {fullName || user?.email}
              </Typography>
              <Typography fontSize={11} color="text.secondary" className="capitalize">
                {String(user?.roles || "").replace(/_/g, " ").toLowerCase()}
              </Typography>
            </Box>
            <KeyboardArrowDown fontSize="small" className="text-[rgba(0,0,0,0.6)]" />
          </Box>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 0,
              className: "mt-2 min-w-[230px] rounded-xl border border-solid border-[#e6eaf2] shadow-[0_18px_40px_-12px_rgba(11,52,116,0.3)]",
            }}
          >
            <Box px={2} py={1.5}>
              <Typography fontWeight={600} fontSize={14} noWrap>
                {fullName || "Signed in"}
              </Typography>
              <Typography fontSize={12} color="text.secondary" noWrap>
                {user?.email}
              </Typography>
              {user?.roles ? (
                <Chip
                  label={String(user.roles).replace(/_/g, " ")}
                  size="small"
                  className="mt-2 h-[22px] text-[11px] font-semibold bg-[#eef3fb] text-[#2f6fd6]"
                />
              ) : null}
            </Box>
            <Divider />
            <MenuItem onClick={() => navigate("/profile")} className="py-[9.6px]">
              <ListItemIcon>
                <AccountCircleOutlined fontSize="small" />
              </ListItemIcon>
              My profile
            </MenuItem>
            <MenuItem onClick={() => dispatch(logout())} className="py-[9.6px] text-[#d64545]">
              <ListItemIcon>
                <Logout fontSize="small" className="text-[#d64545]" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>

        <Container className="flex-1 py-6 text-black">{children}</Container>
      </Box>
    </Box>
  );
}
